import React, { useState, useEffect } from 'react';
import Timeline from './Timeline';
import '../../Analysis_A/TextMarkersAnalysis.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const DateRangesReport = ({ communities, dates, getDayStatus }) => {
    const generateRanges = (statusType) => {
        const ranges = [];
        communities.forEach(community => {
            const communityRanges = [];
            let startDate = null;
            let endDate = null;
            dates.forEach(date => {
                const status = getDayStatus(community.id, date);
                const hasStatus = status === statusType;
                if (hasStatus && startDate === null) {
                    startDate = date;
                    endDate = date;
                } else if (hasStatus && startDate !== null) {
                    endDate = date;
                } else if (!hasStatus && startDate !== null) {
                    communityRanges.push({
                        start: startDate,
                        end: endDate,
                        days: Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
                    });
                    startDate = null;
                    endDate = null;
                }
            });
            if (startDate !== null) {
                communityRanges.push({
                    start: startDate,
                    end: endDate,
                    days: Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
                });
            }
            if (communityRanges.length > 0) {
                ranges.push({
                    community: community,
                    ranges: communityRanges
                });
            }
        });
        return ranges;
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('ru-RU');

    const getStatusConfig = (statusType) => {
        switch (statusType) {
            case 'single':
                return { title: 'Данные есть', color: '#16a34a' };
            case 'empty':
                return { title: 'Данных нет', color: '#6b7280' };
            case 'duplicate':
                return { title: 'Есть дубли', color: '#dc2626' };
            default:
                return { title: 'Неизвестно', color: '#6b7280' };
        }
    };

    const renderStatusSection = (statusType) => {
        const ranges = generateRanges(statusType);
        const config = getStatusConfig(statusType);
        return (
            <div className="status-section">
                <h4 style={{ color: config.color }}>
                    {config.title} ({ranges.length} групп)
                </h4>
                {ranges.length === 0 ? (
                    <p className="no-data">Нет данных для отображения</p>
                ) : (
                    ranges.map((item, index) => (
                        <div key={index} className="range-block">
                            <div className="range-header">
                                <span className="range-name">{item.community.name}</span>
                                <span className="range-id">ID: {item.community.id}</span>
                            </div>
                            {item.ranges.map((r, i) => (
                                <div key={i} className="range-row">
                                    <span>{formatDate(r.start)} — {formatDate(r.end)}</span>
                                    <span>{r.days} {r.days === 1 ? 'день' : r.days < 5 ? 'дня' : 'дней'}</span>
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
        );
    };

    return (
        <div className="date-ranges-report">
            <div className="report-block">
                <h4>Отчет по группам</h4>
                {communities.map(community => {
                    const communityData = { single: [], empty: [], duplicate: [] };
                    dates.forEach(date => {
                        const status = getDayStatus(community.id, date);
                        if (status === 'single') communityData.single.push(date);
                        else if (status === 'empty') communityData.empty.push(date);
                        else if (status === 'duplicate') communityData.duplicate.push(date);
                    });
                    const totalDays = dates.length;
                    const dataDays = communityData.single.length + communityData.duplicate.length;
                    const emptyDays = communityData.empty.length;
                    const duplicateDays = communityData.duplicate.length;

                    return (
                        <div key={community.id} className="community-block">
                            <div className="community-header">
                                <div>
                                    <h5>{community.name}</h5>
                                    <p>ID: {community.id}</p>
                                </div>
                                <div className="community-summary">
                                    <div>Всего дней: {totalDays}</div>
                                    <div>С данными: {dataDays} ({Math.round(dataDays / totalDays * 100)}%)</div>
                                </div>
                            </div>
                            <div className="community-grid">
                                <div className="card green">
                                    <div className="card-header">
                                        <span>Данные есть</span>
                                        <span>{communityData.single.length}</span>
                                    </div>
                                    <div className="card-footer">
                                        {communityData.single.length > 0 ?
                                            `${formatDate(communityData.single[0])} — ${formatDate(communityData.single[communityData.single.length - 1])}` :
                                            'Нет данных'}
                                    </div>
                                </div>

                                <div className="card gray">
                                    <div className="card-header">
                                        <span>Нет данных</span>
                                        <span>{emptyDays}</span>
                                    </div>
                                    <div className="card-footer">
                                        {emptyDays > 0 ?
                                            `${formatDate(communityData.empty[0])} — ${formatDate(communityData.empty[emptyDays - 1])}` :
                                            'Все дни заполнены'}
                                    </div>
                                </div>

                                <div className="card red">
                                    <div className="card-header">
                                        <span>Есть дубли</span>
                                        <span>{duplicateDays}</span>
                                    </div>
                                    <div className="card-footer">
                                        {duplicateDays > 0 ?
                                            `${formatDate(communityData.duplicate[0])} — ${formatDate(communityData.duplicate[duplicateDays - 1])}` :
                                            'Дубли не найдены'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="report-detailed">
                <h4>Детальный отчет по типам данных</h4>
                {renderStatusSection('single')}
                {renderStatusSection('empty')}
                {renderStatusSection('duplicate')}
            </div>
        </div>
    );
};

const TextMarkersAnalysis = () => {
    const [data, setData] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [allCommunities, setAllCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedCommunities, setSelectedCommunities] = useState([]);
    const [hoveredItem, setHoveredItem] = useState(null);

    useEffect(() => {
        fetchTextMarkersData();
    }, [dateRange, selectedCommunities]);

    const fetchTextMarkersData = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({
                start_date: dateRange.start,
                end_date: dateRange.end
            });
            selectedCommunities.forEach(id => params.append('group_id', id));
            const response = await apiFetch(`${API_BASE_URL}/monitoring/text-markers-coverage?${params}`);
            if (!response.ok) throw new Error('Не удалось загрузить данные');
            const result = await response.json();
            setData(result.data || []);
            setCommunities(result.communities || []);
            setAllCommunities(result.all_communities || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDateRange = () => {
        const dates = [];
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1))
            dates.push(new Date(d).toISOString().split('T')[0]);
        return dates;
    };

    const getDayStatus = (groupId, date) => {
        const dateTimestamp = Math.floor(new Date(date).getTime() / 1000);
        const dayData = data.filter(i => i.group_id === groupId && i.date === dateTimestamp);
        if (dayData.length === 0) return 'empty';
        if (dayData.length === 1) return 'single';
        return 'duplicate';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'empty': return 'bg-gray';
            case 'single': return 'bg-green';
            case 'duplicate': return 'bg-red';
            default: return 'bg-gray';
        }
    };

    const getStatusTooltip = (groupId, date) => {
        const dateTimestamp = Math.floor(new Date(date).getTime() / 1000);
        const dayData = data.filter(i => i.group_id === groupId && i.date === dateTimestamp);
        if (dayData.length === 0) return `Нет данных для ${date}`;
        const uniqueSessions = [...new Set(dayData.map(i => i.session_id))];
        return `
            Дата: ${date}
            Записей: ${dayData.length}
            Уникальных session_id: ${uniqueSessions.length}
            ${dayData.length > 1 ? '⚠️ Обнаружены дубли' : ''}
        `;
    };

    const dateRangeOptions = [
        { label: '7 дней', days: 7 },
        { label: '30 дней', days: 30 },
        { label: '90 дней', days: 90 },
        { label: '180 дней', days: 180 },
        { label: '365 дней', days: 365 }
    ];

    const handleDateRangeChange = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    };

    const dates = generateDateRange();

    return (
        <div className="text-markers-analysis">
            <div className="text-markers-header">
                <h2><i className="fas fa-file-text"></i> Анализ заполненности текстовых маркеров</h2>
                <p>Визуализация наличия данных по группам и датам</p>
            </div>

            <div className="text-markers-controls">
                <div className="range-buttons">
                    {dateRangeOptions.map(opt => (
                        <button
                            key={opt.days}
                            className={((new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24)) === opt.days ? 'active' : ''}
                            onClick={() => handleDateRangeChange(opt.days)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                <div className="custom-range">
                    <label>Пользовательский диапазон</label>
                    <input type="date" value={dateRange.start}
                        onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} />
                    <span>—</span>
                    <input type="date" value={dateRange.end}
                        onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} />
                </div>

                <div className="group-filter-wrapper">
                    <label>Фильтр по группам ({allCommunities.length} групп)</label>
                    <div className="group-filter">
                        {allCommunities.map(c => (
                            <label key={c.id}>
                                <input
                                    type="checkbox"
                                    checked={selectedCommunities.includes(c.id)}
                                    onChange={e => {
                                        if (e.target.checked)
                                            setSelectedCommunities(p => [...p, c.id]);
                                        else
                                            setSelectedCommunities(p => p.filter(id => id !== c.id));
                                    }}
                                />
                                <span>{c.name}</span>
                            </label>
                        ))}
                    </div>
                    {selectedCommunities.length > 0 && (
                        <div className="clear-filter" onClick={() => setSelectedCommunities([])}>
                            Очистить фильтр
                        </div>
                    )}
                </div>

                <button className="refresh-button" onClick={fetchTextMarkersData}>
                    <i className="fas fa-sync-alt"></i> Обновить
                </button>

                <div className="legend">
                    <div className="legend-item"><div className="legend-color bg-gray"></div>Нет данных</div>
                    <div className="legend-item"><div className="legend-color bg-green"></div>Одно значение</div>
                    <div className="legend-item"><div className="legend-color bg-red"></div>Дубли</div>
                </div>
            </div>

            {loading && <div className="loading-state"><i className="fas fa-circle-notch fa-2x"></i> Загрузка данных...</div>}
            {error && <div className="error-state"><i className="fas fa-exclamation-triangle"></i> {error}</div>}

            {!loading && !error && (
                <div className="text-markers-body">
                    <Timeline
                        communities={communities}
                        dates={dates}
                        data={data}
                        getDayStatus={getDayStatus}
                        getStatusColor={getStatusColor}
                        getStatusTooltip={getStatusTooltip}
                        hoveredItem={hoveredItem}
                        setHoveredItem={setHoveredItem}
                    />

                    <div className="summary-grid">
                        <div className="summary-card"><h3>Всего групп</h3><p>{communities.length}</p></div>
                        <div className="summary-card"><h3>Дней в диапазоне</h3><p>{dates.length}</p></div>
                        <div className="summary-card"><h3>Записей с данными</h3><p>{data.length}</p></div>
                        <div className="summary-card"><h3>Дней с дублями</h3>
                            <p className="red">
                                {(() => {
                                    const d = new Set();
                                    communities.forEach(c => dates.forEach(date => {
                                        if (getDayStatus(c.id, date) === 'duplicate') d.add(date);
                                    }));
                                    return d.size;
                                })()}
                            </p>
                        </div>
                    </div>

                    <div className="date-report">
                        <h3>Отчет по диапазонам дат</h3>
                        <DateRangesReport
                            communities={communities}
                            dates={dates}
                            getDayStatus={getDayStatus}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TextMarkersAnalysis;
